import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Check, Mail, ToggleLeft, ToggleRight } from 'lucide-react'
import { AxiosError } from 'axios'

import api from '@/services/api'

/**
 * Originations mailbox controls (register P3).
 *
 * The commitment is that Antarctica governs when live mail flows into the
 * platform — themselves, without a deployment and without waiting on Helios.
 * The switches and the audited API were built; nothing rendered them, so the
 * only way to turn collection off was a hand-made PATCH with a bearer token.
 * That is not a switch anyone can reach.
 *
 * The status line is deliberate. `mailboxActive` is an administrator's choice
 * and `mailboxConfigured` is an environment fact they cannot change, and the
 * one read-only strip that existed reported the second as the reason for the
 * first — telling an administrator who had switched collection OFF that the
 * deployment was missing credentials. A control that misreports its own state
 * is worse than no control.
 */

type IntakeSettings = {
  mailboxActive: boolean
  autoPromote: boolean
  crmAutoCreate: boolean
  managerQueueEnabled: boolean
  mailbox: string | null
  mailboxConfigured: boolean
  collecting: boolean
}

const SWITCHES = [
  {
    key: 'mailboxActive' as const,
    label: 'Collect mail from the originations mailbox',
    desc: 'Reads new messages on a schedule. Turn off to stop collection without a deployment; takes effect within one polling interval.',
  },
  {
    key: 'autoPromote' as const,
    label: 'Create deals automatically',
    desc: 'A parsed record becomes a new pipeline deal without a person reviewing it first. Off (the default) means every new emailed deal waits in the queue until a person approves it; a reply on a deal that already exists still reaches that deal.',
  },
  {
    key: 'managerQueueEnabled' as const,
    label: 'Manager approval of confirmed data',
    desc: 'A confirmation by someone who cannot approve also waits for a manager, who approves it or returns it with a note. Off means a confirmation stands on its own, with who and when recorded.',
  },
]

export default function IntakeMailSettings() {
  const [settings, setSettings] = useState<IntakeSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.get('/settings/intake')
      setSettings(res.data as IntakeSettings)
      setError(null)
    } catch (err) {
      const status = (err as AxiosError)?.response?.status
      setError(
        status === 403
          ? 'You need an admin role to change mail collection. These settings are read-only for you.'
          : 'Mail settings could not be read. Nothing has been changed.',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function toggle(key: (typeof SWITCHES)[number]['key']) {
    if (!settings) return
    const next = !settings[key]
    setBusy(key)
    // Optimistic, then reconciled against what the server reports back — the
    // switch must show what is true, not what was asked for.
    setSettings({ ...settings, [key]: next })
    try {
      const res = await api.patch('/settings/intake', { [key]: next })
      setSettings(res.data as IntakeSettings)
      setError(null)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setSettings({ ...settings })
      const status = (err as AxiosError)?.response?.status
      setError(
        status === 403
          ? 'You need an admin role to change mail collection. Nothing was changed.'
          : 'That change could not be saved. Nothing was changed.',
      )
    } finally {
      setBusy(null)
    }
  }

  if (loading) {
    return <div className="h-24 max-w-md animate-pulse rounded-lg border border-amber-400/10 bg-black/20" />
  }

  if (!settings) {
    return (
      <div className="flex max-w-md items-start gap-2 rounded-lg border border-red-400/20 bg-red-400/[0.04] px-3 py-2.5 text-[11px] text-red-300">
        <AlertTriangle size={14} className="mt-px flex-shrink-0" />
        <span>{error ?? 'Mail settings are unavailable.'}</span>
      </div>
    )
  }

  return (
    <div className="max-w-md space-y-2.5">
      {/* What is actually happening, and — where it differs — why. */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5">
        <Mail size={14} className="mt-px flex-shrink-0 text-stone-400" />
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-stone-200">
            {settings.collecting ? 'Collecting mail' : 'Not collecting mail'}
            {settings.mailbox && (
              <span className="ml-1.5 font-normal text-stone-500">{settings.mailbox}</span>
            )}
          </div>
          <div className="text-[10px] text-stone-500">
            {settings.collecting
              ? 'New messages are read on a schedule and appear under Emailed Intake.'
              : settings.mailboxActive
                ? 'Collection is switched on, but this deployment has no mailbox credentials — that is set by Helios, not here.'
                : 'Collection is switched off below. Mail already received is unaffected.'}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-emerald-400/15 bg-emerald-400/[0.04] px-3 py-2.5 text-[11px] text-stone-400">
        <span className="font-medium text-emerald-300">Broker creation requires confirmation.</span>{' '}
        Intake may suggest a brokerage or contact at the point of a miss, but it cannot create either automatically.
      </div>

      {SWITCHES.map((s) => (
        <div
          key={s.key}
          className="flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5"
        >
          <div className="min-w-0 pr-3">
            <div className="text-[12px] font-medium text-stone-200">{s.label}</div>
            <div className="text-[10px] text-stone-500">{s.desc}</div>
          </div>
          <button
            type="button"
            onClick={() => void toggle(s.key)}
            disabled={busy !== null}
            aria-pressed={settings[s.key]}
            aria-label={s.label}
            className="flex-shrink-0 disabled:opacity-50"
          >
            {settings[s.key] ? (
              <ToggleRight size={24} className="text-amber-300" />
            ) : (
              <ToggleLeft size={24} className="text-stone-600" />
            )}
          </button>
        </div>
      ))}

      {error && (
        <div className="flex items-start gap-2 text-[10px] text-red-300">
          <AlertTriangle size={12} className="mt-px flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {saved && !error && (
        <div className="flex items-center gap-1.5 text-[10px] text-emerald-300">
          <Check size={12} /> Saved — every change is recorded with who made it.
        </div>
      )}
    </div>
  )
}
